/**
 * External Links Hook (PostgreSQL)
 *
 * Replaces Convex externalLinks queries/mutations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE = '/api/external-links';

interface Link {
  _id: string;
  name: string;
  url: string;
  description?: string;
  tags: string[];
  sortOrder: number;
}

interface Group {
  _id: string;
  name: string;
  sortOrder: number;
  links: Link[];
}

/**
 * Fetch all groups with their nested links
 */
export function useExternalLinksGroups() {
  return useQuery({
    queryKey: ['externalLinks'],
    queryFn: async (): Promise<Group[]> => {
      const res = await fetch(API_BASE);
      if (!res.ok) {
        throw new Error(`Failed to fetch external links: ${res.status}`);
      }
      return res.json();
    },
    staleTime: 60 * 60 * 1000, // 1 hour - links rarely change
  });
}

/**
 * Create a new group
 */
export function useCreateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      name,
      createdBy,
    }: {
      name: string;
      createdBy: string;
    }) => {
      const res = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'createGroup', name, createdBy }),
      });
      if (!res.ok) {
        throw new Error(`Failed to create group: ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['externalLinks'] });
    },
  });
}

/**
 * Update a group
 */
export function useUpdateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, name, userEmail }: { id: string; name: string; userEmail?: string }) => {
      const res = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'updateGroup', id, name, userEmail }),
      });
      if (!res.ok) {
        throw new Error(`Failed to update group: ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['externalLinks'] });
    },
  });
}

/**
 * Delete a group (and all its links)
 */
export function useDeleteGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, userEmail }: { id: string; userEmail?: string }) => {
      const res = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'deleteGroup', id, userEmail }),
      });
      if (!res.ok) {
        throw new Error(`Failed to delete group: ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['externalLinks'] });
    },
  });
}

/**
 * Create a new link
 */
export function useCreateLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      name,
      url,
      description,
      groupId,
      tags,
      createdBy,
    }: {
      name: string;
      url: string;
      description?: string;
      groupId: string;
      tags: string[];
      createdBy: string;
    }) => {
      const res = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'createLink', name, url, description, groupId, tags, createdBy }),
      });
      if (!res.ok) {
        throw new Error(`Failed to create link: ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['externalLinks'] });
    },
  });
}

/**
 * Update a link
 */
export function useUpdateLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      name,
      url,
      description,
      groupId,
      tags,
      userEmail,
    }: {
      id: string;
      name?: string;
      url?: string;
      description?: string;
      groupId?: string;
      tags?: string[];
      userEmail?: string;
    }) => {
      const res = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'updateLink', id, name, url, description, groupId, tags, userEmail }),
      });
      if (!res.ok) {
        throw new Error(`Failed to update link: ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['externalLinks'] });
    },
  });
}

/**
 * Delete a link
 */
export function useDeleteLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, userEmail }: { id: string; userEmail?: string }) => {
      const res = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'deleteLink', id, userEmail }),
      });
      if (!res.ok) {
        throw new Error(`Failed to delete link: ${res.status}`);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['externalLinks'] });
    },
  });
}
